import { type ImportSupply48DataSummary } from '@ebalance/proto/bffuser/v1/supply_48_data_service_pb'
import { useTranslation } from 'react-i18next'

import {
  CsvDiffSummary,
  type CsvDiffSummaryItem,
} from '~/components/common/CsvUpload/CsvDiffSummary'

/**
 * Step2 サマリカードのクリックフィルタ値 (design `#summary .up-summary-card`
 * の `data-filter` に対応)。'all' = 合計 (全行、初期選択)。'diff' = 新規+変更+削除
 * (common.md AC-DUC-012「差分のみ」、D11)。'deleted' = delete=1 (DELETED) 行
 * のみ (AC-DUC-012 の削除フィルタ)。
 */
export type SummaryCardFilter =
  | 'all'
  | 'unchanged'
  | 'new'
  | 'changed'
  | 'deleted'
  | 'diff'
  | 'error'
  | 'warning'

type Props = {
  summary: ImportSupply48DataSummary
  activeFilter: SummaryCardFilter
  onFilterChange: (filter: SummaryCardFilter) => void
}

/**
 * Step2 差分プレビューのサマリカード (design `admin/uploads/supply-48data.html`
 * L195-229 準拠。ただし design は delete 列を持たない旧版で stale なため、
 * 削除カードの visual は `bilateral-trade.html` の削除カード先例に倣う)。
 * 「変更なし / 新規追加 / 変更 / 削除 / エラー (0 件時は非表示) / 時間帯区分整合の
 * 警告 (AC-S4D-045、0 件時は非表示) / 差分のみ (新規+変更+削除、common.md
 * AC-DUC-012、D11) / 処理対象合計」の最大 8 枚をクリック式フィルタとして表示する。
 * 削除カードは変更/新規と同様に 0 件でも常時表示する (AC-DUC-013)。
 *
 * 件数はすべて summary から表示する。処理対象合計は
 * new+changed+unchanged+deleted の表示合成であり、サーバー値の再計算ではない
 * (エラーは処理対象外のため合計に含めない。common.md「『合計』件数表示は
 * 『処理対象合計』=『変更なし + 新規 + 変更 + 削除』とし、エラー行は対象外とする」に
 * 合わせ、ラベル自体も「処理対象合計」にしてエラー込み表示行数との食い違いを防ぐ)。
 *
 * 見た目 (segmented 準拠の pill・件数の色) と選択状態の表現 (`role="button"` +
 * `aria-pressed`) は CSV アップロード共通の `CsvDiffSummary` が持つ。
 */
export const SummaryCards = ({
  summary,
  activeFilter,
  onFilterChange,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const total =
    summary.newRowCount +
    summary.changedRowCount +
    summary.unchangedRowCount +
    summary.deletedRowCount

  const cards: Array<CsvDiffSummaryItem<SummaryCardFilter>> = [
    {
      filter: 'unchanged',
      label: t('page:supply48DataCsvUpload.step2.summary.unchanged'),
      count: summary.unchangedRowCount,
    },
    {
      filter: 'new',
      label: t('page:supply48DataCsvUpload.step2.summary.new'),
      count: summary.newRowCount,
      tone: 'positive',
    },
    {
      filter: 'changed',
      label: t('page:supply48DataCsvUpload.step2.summary.changed'),
      count: summary.changedRowCount,
      tone: 'warning',
    },
    {
      filter: 'deleted',
      label: t('page:supply48DataCsvUpload.step2.summary.deleted'),
      count: summary.deletedRowCount,
      tone: 'negative',
    },
    ...(summary.errorRowCount > 0
      ? [
          {
            filter: 'error' as const,
            label: t('page:supply48DataCsvUpload.step2.summary.error'),
            count: summary.errorRowCount,
            tone: 'negative' as const,
          },
        ]
      : []),
    // AC-S4D-045: 時間帯区分整合の警告を含む行数。 エラーと違って反映を妨げない
    // が、 このカードで存在を可視化する (フィルタで該当行だけ絞り込める)。
    ...(summary.warningRowCount > 0
      ? [
          {
            filter: 'warning' as const,
            label: t('page:supply48DataCsvUpload.step2.summary.warning'),
            count: summary.warningRowCount,
            tone: 'warning' as const,
          },
        ]
      : []),
    {
      filter: 'diff',
      label: t('page:supply48DataCsvUpload.step2.summary.diff'),
      count:
        summary.newRowCount + summary.changedRowCount + summary.deletedRowCount,
    },
    {
      filter: 'all',
      label: t('page:supply48DataCsvUpload.step2.summary.total'),
      count: total,
    },
  ]

  return (
    <CsvDiffSummary
      activeFilter={activeFilter}
      items={cards}
      onFilterChange={onFilterChange}
    />
  )
}
